import { clearPendingLogout, forgetPendingSignIn, forgetUser, logout, markPendingLogout } from "./auth.js";

/**
 * Sign out: end the session, then clear everything this device holds.
 *
 * Both halves matter, and in this order. The session is what the server knows;
 * the mirror and the queue are what this device holds -- a failed logout must
 * not leave the next person on this phone looking at someone else's lists.
 *
 * The wait on the server is bounded, and that is the point of the race rather
 * than a nicety. `fetch` has no timeout of its own, and a stalled connection is
 * not an offline one: it neither resolves nor rejects, so awaiting it plainly
 * would leave someone staring at their own lists on a device that still holds
 * them. Telling the server is worth a few seconds. It is not worth the wipe.
 *
 * What the race gives up is written down first (g65). The note that a sign-out
 * is pending goes in before the request leaves, and only the server's answer
 * clears it; until then every start and every sign-in sends the logout again,
 * so the HttpOnly cookie the wipe cannot reach never reopens the account. The
 * remembered user goes at the same moment, not after the race: another tab
 * that starts during the wait would otherwise open the mirror being wiped.
 *
 * `leave` is last and is a full navigation: it is the only way to be certain
 * nothing from the previous session is still held in a closure somewhere.
 * `location.href` does not wait for IndexedDB, so it cannot come first.
 */
export const LOGOUT_WAIT_MS = 3_000;

export interface Wipe {
  /** Deletes the mirror and the queue. */
  destroy(): Promise<void>;
  /** Leaves for a fresh document. */
  leave(): void;
}

export async function signOutAndWipe(wipe: Wipe, waitMs = LOGOUT_WAIT_MS): Promise<void> {
  forgetPendingSignIn();
  const pending = markPendingLogout();
  forgetUser();
  await Promise.race([
    // Sent even when storage refused the note: only the retry is lost then.
    logout().then(() => clearPendingLogout(pending), () => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, waitMs)),
  ]);
  await wipe.destroy().catch(() => undefined);
  wipe.leave();
}
